import React from "react";
import { Box, Card, CardContent, Typography, Grid, Chip, IconButton } from "@mui/material"; // Import Chip
import "./StandardCardView.css";
import ActionMenu from "../../ActionMenu/ActionMenu";
import { useLocation } from "react-router-dom";

const StandardCardView = ({ allData, actions, isEdit, open, onClose, cardViewFields, onActionProjectTimeLine }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const location = useLocation();
  const isFocusMode = location.pathname === "/focus-projecttimeline";

  const handleMenuOpen = (event, row) => {
    event.stopPropagation(); // Prevent card click when clicking menu
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCardClick = (item) => {
    if (isFocusMode) {
      // In focus mode, trigger project timeline
      onActionProjectTimeLine(item);
    } else {
      // In normal mode, trigger overview
      const overviewAction = actions?.find(action => action.label === "Overview");
      if (overviewAction) {
        overviewAction.onClick(item);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].split("-").join("/");
  };

  const renderFieldValue = (item, field, customRenderer) => {
    // If a custom renderer is provided, use it
    if (customRenderer) {
      return customRenderer(item[field.field] || item, item);
    }

    // Otherwise use default renderers
    switch (field.field) {
      case "start_date":
      case "end_date":
        return formatDate(item[field.field]);
      case "status":
        return (
          <Box
            sx={{
              backgroundColor: item[field.field] === "Completed" ? "#EBFFF8" : 
                             item[field.field] === "In Progress" ? "#FFFBEB" : "#f1f1f2",
              color: item[field.field] === "Completed" ? "#00CC85" :
                    item[field.field] === "In Progress" ? "#F6C000" : "#7e8299",
              padding: "4px 12px",
              borderRadius: "16px",
              display: "inline-block",
              fontSize: "0.875rem",
              fontWeight: 500
            }}
          >
            {item[field.field]}
          </Box>
        );
      case "file":
        return item[field.field]?.sas_url ? (
          <img
            src={item[field.field].sas_url}
            alt="Logo"
            style={{
              width: "60px",
              height: "60px",
              objectFit: "contain"
            }}
          />
        ) : null;
      default:
        return item[field.field] || "-";
    }
  };

  return (
    <Box>
      <Grid container spacing={3} justifyContent="flex-start">
        {allData?.length ? (
          allData.map((item) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={item.id}
              style={{ display: "flex" }}
            >
              <Card
                sx={{
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  padding: "16px",
                  position: "relative",
                  overflow: "visible",
                  minHeight: "320px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  '&:hover': {
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#FAFAFA"
                  }
                }}
                onClick={() => handleCardClick(item)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {/* Framework Logo */}
                  {item?.file?.sas_url && (
                    <Box 
                      sx={{ 
                        width: "90%",
                        display: "flex",
                        justifyContent: "flex-start",
                        marginBottom: "10px"
                      }}
                    >
                      <img
                        src={item?.file?.sas_url}
                        alt="framework_logo"
                        style={{ 
                          maxWidth: "300px", 
                          height: "60px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Organization Info */}
                  {!item?.file?.sas_url && (
                    <Box 
                      sx={{ 
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        marginBottom: "10px"
                      }}
                    >
                      {/* Organization Logo */}
                      {item?.logo1_sas_url ? (
                        <img
                          src={item?.logo1_sas_url}
                          alt="org_img"
                          style={{ 
                            width: "40px", 
                            height: "40px"
                          }}
                        />
                      ) : (
                        <Box 
                          sx={{ 
                            width: "40px", 
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f1f3f5",
                            borderRadius: "4px"
                          }}
                        >
                          <img
                            src="assets/icons/organization.png"
                            alt="org_default"
                            style={{ 
                              width: "24px", 
                              height: "24px"
                            }}
                          />
                        </Box>
                      )}
                      
                      {/* Organization Name and Framework Name */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, overflow: "hidden", maxWidth: "calc(100% - 80px)" }}>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item?.organization_name || "-"}
                        </Typography>
                        {item?.framework_name && (
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#6c757d",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item?.framework_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                
                {/* Menu Icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "16px",
                    right: "16px",
                    zIndex: 1
                  }}
                >
                  <img
                    onClick={(event) => handleMenuOpen(event, item)}
                    src="assets/icons/verticalMoreIcon.png"
                    className="moreIconStandardCardView"
                    alt="verticalMoreIcon"
                  />
                </Box>
                <CardContent sx={{ padding: 0, flexGrow: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.5,
                      width: "100%",
                      wordBreak: "break-word",
                      whiteSpace: "normal"
                    }}
                  >
                    {item?.name}
                  </Typography>
                  {item?.name_acronym && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem",
                        color: "#6c757d",
                        marginTop: "-8px"
                      }}
                    >
                      Code: {item?.name_acronym}
                    </Typography>
                  )}
                  {item?.description && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          marginBottom: "4px"
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color: "#6c757d",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.5,
                          width: "100%",
                          wordBreak: "break-word",
                          whiteSpace: "normal"
                        }}
                      >
                        {item?.description}
                      </Typography>
                    </Box>
                  )}
                  {cardViewFields.some(field => field.field === "scope_of_work") && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          marginBottom: "4px"
                        }}
                      >
                        Scope of Work
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color: "#6c757d",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.5,
                          width: "100%",
                          wordBreak: "break-word",
                          whiteSpace: "normal"
                        }}
                      >
                        {item?.scope_of_work || "-"}
                      </Typography>
                    </Box>
                  )}
                  {(cardViewFields.some(field => field.field === "start_date") || cardViewFields.some(field => field.field === "end_date")) && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {cardViewFields.some(field => field.field === "start_date") && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              marginBottom: "4px"
                            }}
                          >
                            Start Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.875rem",
                              color: "#6c757d"
                            }}
                          >
                            {formatDate(item?.start_date) || "-"}
                          </Typography>
                        </Box>
                      )}
                      {cardViewFields.some(field => field.field === "end_date") && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              marginBottom: "4px"
                            }}
                          >
                            End Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.875rem",
                              color: "#6c757d"
                            }}
                          >
                            {formatDate(item?.end_date) || "-"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {cardViewFields.some(field => field.field === "status") && item.status && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          marginBottom: "4px"
                        }}
                      >
                        Status
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: item.status === "Completed" ? "#EBFFF8" : 
                                         item.status === "In Progress" ? "#FFFBEB" : "#f1f1f2",
                          color: item.status === "Completed" ? "#00CC85" :
                                item.status === "In Progress" ? "#F6C000" : "#7e8299",
                          padding: "4px 12px",
                          borderRadius: "16px",
                          display: "inline-block",
                          fontSize: "0.875rem",
                          fontWeight: 500
                        }}
                      >
                        {item.status}
                      </Box>
                    </Box>
                  )}

                  {/* Custom Fields with Renderers */}
                  {cardViewFields.map((field, index) => {
                    // Skip fields that are already handled above
                    if (['file', 'name', 'name_acronym', 'description', 'scope_of_work', 'start_date', 'end_date', 'status'].includes(field.field)) {
                      return null;
                    }
                    
                    // Skip fields that don't have a renderer
                    if (!field.render) {
                      return null;
                    }

                    return (
                      <Box key={index}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            marginBottom: "4px"
                          }}
                        >
                          {field.label}
                        </Typography>
                        <Box>
                          {field.render(item[field.field], item)}
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box sx={{ width: "100%", textAlign: "center", padding: "20px" }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </Grid>
      <ActionMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        actions={actions}
        selectedRow={selectedRow}
        isEdit={isEdit}
      />
    </Box>
  );
};

export default StandardCardView;
